import { useState, useMemo, useRef } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import {
	IntentStep,
	FormStep,
	ResultMorStep,
	ResultCalStep,
	ResultHubStep,
	StepIndicator,
	type ProcessingFormData,
} from './steps';
import {
	VOLUME_LT_50K,
	type IntentValue,
} from './post-signup-onboarding-constants';

const STEP_INTENT = 'intent';
const STEP_FORM = 'form';
const STEP_RESULT = 'result';
const STEP_RESULT_CAL = 'result_cal';
const STEP_RESULT_HUB = 'result_hub';

export interface PostSignUpOnboardingProps {
	onComplete: () => void;
	className?: string;
}

const defaultFormData: ProcessingFormData = {
	firstName: '',
	lastName: '',
	storeWebsite: '',
	monthlyVolume: '',
};

type Step =
	| typeof STEP_INTENT
	| typeof STEP_FORM
	| typeof STEP_RESULT
	| typeof STEP_RESULT_CAL
	| typeof STEP_RESULT_HUB;

const STEP_ORDER: Record<Step, number> = {
	[STEP_INTENT]: 0,
	[STEP_FORM]: 1,
	[STEP_RESULT]: 2,
	[STEP_RESULT_CAL]: 2,
	[STEP_RESULT_HUB]: 3,
};

const contentVariants = {
	enter: (direction: number) => ({
		x: direction > 0 ? 60 : -60,
		opacity: 0,
	}),
	center: { x: 0, opacity: 1 },
	exit: (direction: number) => ({
		x: direction > 0 ? -60 : 60,
		opacity: 0,
	}),
};

export function PostSignUpOnboarding({ onComplete, className }: PostSignUpOnboardingProps) {
	const { user } = useUser();
	const { t } = useTranslation('onboarding');
	const [step, setStep] = useState<Step>(STEP_INTENT);
	const [intent, setIntent] = useState<IntentValue | ''>('');
	const [formData, setFormData] = useState<ProcessingFormData>(defaultFormData);
	const directionRef = useRef(1);

	const calPrefill = useMemo(() => {
		const name = [formData.firstName, formData.lastName].filter(Boolean).join(' ');
		const email = user?.primaryEmailAddress?.emailAddress ?? '';
		return { name, email };
	}, [formData.firstName, formData.lastName, user?.primaryEmailAddress?.emailAddress]);

	const goTo = (next: Step) => {
		directionRef.current = STEP_ORDER[next] >= STEP_ORDER[step] ? 1 : -1;
		setStep(next);
	};

	const handleIntentNext = () => {
		if (intent === 'CRM') {
			onComplete();
			return;
		}
		if (intent === 'PROCESSING' || intent === 'BOTH') {
			goTo(STEP_FORM);
		}
	};

	const handleFormNext = () => {
		const isLt50k = formData.monthlyVolume === VOLUME_LT_50K;
		goTo(isLt50k ? STEP_RESULT : STEP_RESULT_CAL);
	};

	const handleCalNext = () => goTo(STEP_RESULT_HUB);
	const handleBackToIntent = () => goTo(STEP_INTENT);
	const handleBackToForm = () => goTo(STEP_FORM);

	const isLt50k = formData.monthlyVolume === VOLUME_LT_50K;
	const totalSteps = intent === 'CRM' ? 1 : isLt50k ? 3 : 4;
	const currentStepNumber =
		step === STEP_INTENT
			? 1
			: step === STEP_FORM
				? 2
				: step === STEP_RESULT || step === STEP_RESULT_CAL
					? 3
					: 4;

	const stepLabels = useMemo(() => {
		const base = [t('steps.intent'), t('steps.form')];
		if (isLt50k) return [...base, t('steps.result')];
		return [...base, t('steps.resultCal'), t('steps.resultHub')];
	}, [t, isLt50k]);

	return (
		<motion.div
			layout
			transition={{ layout: { duration: 0.35, ease: 'easeInOut' } }}
			className={cn(
				'mx-auto w-full max-w-6xl space-y-8 px-4 py-6 text-center',
				className,
			)}
		>
			<StepIndicator step={currentStepNumber} totalSteps={totalSteps} labels={stepLabels} />

			<AnimatePresence mode="wait" custom={directionRef.current}>
				{step === STEP_INTENT && (
					<motion.div
						key={STEP_INTENT}
						custom={directionRef.current}
						variants={contentVariants}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{ duration: 0.25, ease: 'easeInOut' }}
					>
						<IntentStep
							value={intent}
							onChange={setIntent as (v: IntentValue) => void}
							onNext={handleIntentNext}
						/>
					</motion.div>
				)}

				{step === STEP_FORM && (
					<motion.div
						key={STEP_FORM}
						custom={directionRef.current}
						variants={contentVariants}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{ duration: 0.25, ease: 'easeInOut' }}
					>
						<FormStep
							formData={formData}
							onChange={setFormData}
							onNext={handleFormNext}
							onPrev={handleBackToIntent}
						/>
					</motion.div>
				)}

				{step === STEP_RESULT && isLt50k && (
					<motion.div
						key={STEP_RESULT}
						custom={directionRef.current}
						variants={contentVariants}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{ duration: 0.25, ease: 'easeInOut' }}
					>
						<ResultMorStep onComplete={onComplete} onPrev={handleBackToForm} />
					</motion.div>
				)}

				{step === STEP_RESULT_CAL && (
					<motion.div
						key={STEP_RESULT_CAL}
						custom={directionRef.current}
						variants={contentVariants}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{ duration: 0.25, ease: 'easeInOut' }}
					>
						<ResultCalStep prefill={calPrefill} onNext={handleCalNext} onPrev={handleBackToForm} />
					</motion.div>
				)}

				{step === STEP_RESULT_HUB && (
					<motion.div
						key={STEP_RESULT_HUB}
						custom={directionRef.current}
						variants={contentVariants}
						initial="enter"
						animate="center"
						exit="exit"
						transition={{ duration: 0.25, ease: 'easeInOut' }}
					>
						<ResultHubStep onComplete={onComplete} />
					</motion.div>
				)}
			</AnimatePresence>
		</motion.div>
	);
}
